import React, { useState, useEffect } from "react";
import EditPartnerForm from "./EditPartnerForm";
import AddPartnerForm from "./AddPartnerForm";


const PartnerSearch = ({ refreshTrigger }) => {
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showEditPartnerForm, setShowEditPartnerForm] = useState(false);
  const [showAddPartnerForm, setShowAddPartnerForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);




  /**
   * Fetch all partners from the backend
   */
  const fetchPartners = async () => {
    setLoading(true);
    setError(null);

    try {
      
      const response = await fetch('/api/partners', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Partners fetched:', data.length, data);
      
      setPartners (data);
    } catch (e) {
      console.error('❌ Error fetching partners:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };


  /**
   * Delete a Partner
   */
  const handleDeletePartner = async (partner) => {
    const PartnerId = partner.id || partner.asset2_id || partner.partner_id;
    const partnerName = partner.name || partner.asset2_name || partner.partner_name;
    
    if (!window.confirm(`Are you sure you want to delete "${partnerName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting partner:', PartnerId);
      
      const response = await fetch(`/api/partners/${PartnerId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete partner: ${response.statusText}`);
      }

      console.log('✅ Partner deleted successfully');
      
      // Refresh the partner list
      fetchPartners();
      
      // Optional: Show success message
      alert(`Partner "${partnerName}" deleted successfully!`);
    } catch (error) {
      console.error('❌ Error deleting partner:', error);
      alert(`Error deleting partner: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [refreshTrigger]);

  // Filter partners based on search query
  const filteredPartners = partners.filter(partner => {
    const searchLower = searchQuery.toLowerCase();
    
    // Handle both naming conventions: asset2_name/asset2_code OR partner_name/partner_code
    const name = partner.name || partner.asset2_name || partner.partner_name || '';
    const code = partner.number || partner.asset2_code || partner.partner_code || '';
    
    return (
      name.toLowerCase().includes(searchLower) ||
      code.toLowerCase().includes(searchLower)
    );
  });

  const handleAddPartner = () => setShowAddPartnerForm(true);

  const handleEditPartner = (partner) => { 
    setSelectedPartner(partner); 
    setShowEditPartnerForm(true); 
  };

  const handleSavePartner = () => { 
    fetchPartners(); 
    setShowEditPartnerForm(false); 
  };

  const handlePartnerAdded = () => { 
    fetchPartners(); 
    setShowAddPartnerForm(false); 
  };

  return (
    <div className="user-list-container">
      <h2>Partner List</h2>

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '15px',
          margin: '10px 0',
          backgroundColor: '#fee',
          border: '1px solid #c33',
          borderRadius: '4px',
          color: '#c33'
        }}>
          <strong>⚠️ Error:</strong> {error}
          <button 
            onClick={fetchPartners}
            style={{
              marginLeft: '10px',
              padding: '5px 10px',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      )}

      {/* Search Section */}
      <div className="search-section">
        <label>Search Partners:</label>
        <input
          type="text"
          placeholder="Search by name or code"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
        />
      </div>

      {/* Add Button */}
      <div className="button-container">
        <button 
          className="add-partner-button" 
          onClick={handleAddPartner}
          disabled={loading}
        >
          + Add Partner
        </button>
      </div>

      {/* Partners Table */}
      <div className="UserList-container">
        <table>
          <thead>
            <tr>
              <th>Partner Name</th>
              <th>Partner Code</th>
              <th>Preview</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                  Loading partners...
                </td>
              </tr>
            ) : filteredPartners.length > 0 ? (
              filteredPartners.map((partner) => {
                // Handle different naming conventions from backend
                const partnerId = partner.id || partner.asset2_id || partner.partner_id;
                const partnerName = partner.name || partner.asset2_name || partner.partner_name;
                const partnerCode = partner.number || partner.asset2_code || partner.partner_code;

                return (
                  <tr key={partnerId}>
                    <td>{partnerName}</td>
                    <td>{partnerCode || '-'}</td>
                    <td>
                      {partnerCode && partnerCode.startsWith('#') ? (
                        <div 
                          style={{
                            width: '50px',
                            height: '30px',
                            backgroundColor: partnerCode,
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            margin: '0 auto'
                          }}
                          title={partnerCode}
                        />
                      ) : (
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          {partnerCode || 'No preview'}
                        </span>
                      )}
                    </td>
                    <td>
                      <button onClick={() => handleEditPartner(partner)}>
                        Edit
                      </button>

                      {/* Delete button - only shown if user has permission */}
                      
                        <button disabled={!permissions.includes("partner_delete_button")}
                          onClick={() => handleDeletePartner(partner)}
                          style={{ 
                            marginLeft: '5px', 
                            backgroundColor: '#dc3545', 
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                        >
                          Delete
                        </button>
                      
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                  {searchQuery 
                    ? 'No partners match your search.' 
                    : 'No partners found. Add one to get started!'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total Count */}
      {!loading && filteredPartners.length > 0 && (
        <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
          Showing {filteredPartners.length} of {partners.length} partners
        </div>
      )}

      {/* Edit Modal */}
      {showEditPartnerForm && selectedPartner && (
        <EditPartnerForm
          partner={selectedPartner}
          onClose={() => setShowEditPartnerForm(false)}
          onSave={handleSavePartner}
        />
      )}

      {/* Add Modal */}
      {showAddPartnerForm && (
        <AddPartnerForm
          onClose={() => setShowAddPartnerForm(false)}
          onPartnerAdded={handlePartnerAdded}
        />
      )}
    </div>
  );
};

export default PartnerSearch;